import { UzButton } from '@utazzo/ui';
import { Check } from 'lucide-react';
import React from 'react';

export interface Step {
  id: number;
  label: string;
  description: string;
}

export const STEPS: Step[] = [
  { id: 0, label: 'Profile', description: 'Core identity and contact details' },
  { id: 1, label: 'Addresses', description: 'Billing and home addresses' },
  { id: 2, label: 'Contacts', description: 'Secondary contacts and dependants' },
  { id: 3, label: 'Documents', description: 'Passport, PAN, visa and other documents' },
];

interface CustomerStepperHeaderProps {
  currentStep: number;
  /** Steps 1-3 require a customer id to exist before they are usable. */
  customerId: string | null;
}

/**
 * Horizontal step indicator. Steps 1-3 are visually disabled until a customer
 * record has been created (id-first design - nested sub-resources need the id).
 */
export const CustomerStepperHeader = ({
  currentStep,
  customerId,
}: CustomerStepperHeaderProps): React.JSX.Element => (
  <nav aria-label="Customer form steps">
    <ol className="flex items-center gap-0">
      {STEPS.map((step, index) => {
        const isCompleted = currentStep > step.id;
        const isCurrent = currentStep === step.id;
        const isLocked = step.id > 0 && !customerId;

        return (
          <React.Fragment key={step.id}>
            <li className="flex items-center">
              <div
                className={['flex flex-col items-center gap-1', isLocked ? 'opacity-40' : ''].join(
                  ' ',
                )}
              >
                <div
                  className={[
                    'flex h-8 w-8 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors',
                    isCompleted
                      ? 'border-primary bg-primary text-primary-foreground'
                      : isCurrent
                        ? 'border-primary bg-background text-primary'
                        : 'bg-background border-zinc-300 text-zinc-400',
                  ].join(' ')}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  {isCompleted ? <Check className="h-4 w-4" /> : step.id + 1}
                </div>
                <span
                  className={[
                    'hidden text-xs font-medium sm:block',
                    isCurrent ? 'text-primary' : 'text-zinc-500',
                  ].join(' ')}
                >
                  {step.label}
                </span>
              </div>
            </li>
            {index < STEPS.length - 1 && (
              <div
                className={[
                  'h-0.5 flex-1 transition-colors',
                  currentStep > index ? 'bg-primary' : 'bg-zinc-200',
                ].join(' ')}
                aria-hidden
              />
            )}
          </React.Fragment>
        );
      })}
    </ol>
  </nav>
);

interface CustomerStepperNavProps {
  currentStep: number;
  totalSteps: number;
  onBack: () => void;
  onNext: () => void;
  onDone: () => void;
  /** Save the profile and return straight to the list (skip the later steps). */
  onSaveAndClose: () => void;
  /** While the profile save is in flight. */
  isSaving: boolean;
  /** Whether step 0 has successfully created / loaded the customer. */
  customerId: string | null;
}

/**
 * Navigation row rendered below each step's content. Step 0 shows
 * "Save & close" + "Save & continue" (a basic customer needs only the
 * Profile, so the later steps are optional); steps 1-3 show "Back" + "Next"
 * (or "Done" on the last step). All navigation through steps 1-3 is instant
 * (no network call) unless the step's own add/remove actions are in flight.
 */
export const CustomerStepperNav = ({
  currentStep,
  totalSteps,
  onBack,
  onNext,
  onDone,
  onSaveAndClose,
  isSaving,
  customerId,
}: CustomerStepperNavProps): React.JSX.Element => {
  const isFirst = currentStep === 0;
  const isLast = currentStep === totalSteps - 1;

  return (
    <div className="mt-6 flex items-center justify-between border-t pt-4">
      <UzButton
        variant="ghost"
        onClick={onBack}
        disabled={isFirst || isSaving}
        aria-label="Go to previous step"
      >
        Back
      </UzButton>

      {isLast ? (
        <UzButton onClick={onDone} disabled={isSaving} aria-label="Finish and return to list">
          Done
        </UzButton>
      ) : isFirst ? (
        <div className="flex items-center gap-2">
          <UzButton
            variant="outline"
            onClick={onSaveAndClose}
            loading={isSaving}
            aria-label="Save profile and return to the list"
          >
            Save &amp; close
          </UzButton>
          <UzButton
            onClick={onNext}
            loading={isSaving}
            aria-label="Save profile and continue to the optional steps"
          >
            Save &amp; continue
          </UzButton>
        </div>
      ) : (
        <UzButton onClick={onNext} disabled={!customerId || isSaving} aria-label="Go to next step">
          Next
        </UzButton>
      )}
    </div>
  );
};
